import { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useProvince } from '../hooks/useProvince';
import { useStrategyGuidance } from '../hooks/useStrategyGuidance';
import { useToast } from '../hooks/useToast';
import { useAppShellPlatform } from '../hooks/useAppShellPlatform';
import { Spinner } from '../components/Spinner';
import { ErrorMessage } from '../components/ErrorMessage';
import { MobileSciencesShell } from '../components/sciences/MobileSciencesShell';
import { DesktopSciencesShell } from '../components/sciences/DesktopSciencesShell';
import { computeFillStrategyBooks } from '../utils/fillStrategyBooks';
import { formatNumber } from '../utils/format';
import { api } from '../api/client';
import type { Province, ScienceInvestment } from '../types/province';
import type { ActiveStrategyDto } from '../types/strategy';
import type {
  ScienceSettingsRequest,
  ScienceSettingsResult,
  ScienceType,
  InvestInScienceRequest,
  InvestScienceResponse,
} from '../types/science';

const EMPTY_BOOKS: Record<ScienceType, number> = {
  ALCHEMY: 0, TOOLS: 0, HOUSING: 0, FOOD: 0,
  MILITARY: 0, THIEVERY: 0, CHANNELING: 0,
};

/**
 * SF-161: a single shared tab enum, not a two-enum split like TrainingShellProps — Science's tab
 * membership (Invest/Overview) is identical on both platforms, so a breakpoint flip mid-edit keeps
 * the same tab selected instead of resetting it. See spec Component 4.
 */
export type SciencesTabKey = 'invest' | 'overview';

/**
 * SF-140: named, fully-typed props shared by MobileSciencesShell/DesktopSciencesShell — same
 * BuildingsShellProps/ExploreShellProps precedent. State stays hoisted here so a breakpoint flip
 * mid-edit doesn't lose the in-progress `booksToInvest` draft.
 */
export interface SciencesShellProps {
  province: Province;
  activeStrategy: ActiveStrategyDto | null;
  strategyLoading: boolean;
  activeTab: SciencesTabKey;
  setActiveTab: (tab: SciencesTabKey) => void;
  researchRate: string;
  booksToInvest: Record<ScienceType, number>;
  isSubmittingSettings: boolean;
  isSubmittingInvestment: boolean;
  submitError: string | null;
  successMessage: string | null;
  handleResearchRateChange: (value: string) => void;
  handleSubmitSettings: () => void;
  handleBooksToInvestChange: (scienceType: string, value: number) => void;
  handleSubmitInvestment: () => void;
  handleFillStrategy: () => void;
  handleClearInvestment: () => void;
  researchRateOptions: Province['sciences']['researchRateOptions'];
  scienceTypes: Province['sciences']['scienceTypes'];
  unallocatedBooks: number;
  investmentProgress: Record<string, number>;
  investments: ScienceInvestment[];
  calculatedValues: { booksPerTick: number; goldCost: number; costPercentage: number };
  totalBooksToInvest: number;
  investmentValid: boolean;
}

export function Science() {
  const { provinceId, worldId } = useAuth();
  const { province, isLoading, error, refresh } = useProvince();
  const { activeStrategy, isLoading: strategyLoading } = useStrategyGuidance(worldId, provinceId);
  const { addToast } = useToast();
  const platform = useAppShellPlatform();

  const [activeTab, setActiveTab] = useState<SciencesTabKey>('invest');
  const [researchRate, setResearchRate] = useState<string>('NO_RESEARCH');
  const [allocations, setAllocations] = useState<Record<ScienceType, number>>({
    ALCHEMY: 14.29, TOOLS: 14.29, HOUSING: 14.28, FOOD: 14.28,
    MILITARY: 14.28, THIEVERY: 14.29, CHANNELING: 14.29,
  });
  const [booksToInvest, setBooksToInvest] = useState<Record<ScienceType, number>>(EMPTY_BOOKS);
  const [isSubmittingSettings, setIsSubmittingSettings] = useState(false);
  const [isSubmittingInvestment, setIsSubmittingInvestment] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const hasInitialized = useRef(false);

  useEffect(() => {
    if (province?.sciences && !hasInitialized.current) {
      hasInitialized.current = true;
      setResearchRate(province.sciences.researchRate);
      const alloc = province.sciences.allocation;
      setAllocations({
        ALCHEMY: alloc.ALCHEMY ?? 14.29, TOOLS: alloc.TOOLS ?? 14.29,
        HOUSING: alloc.HOUSING ?? 14.28, FOOD: alloc.FOOD ?? 14.28,
        MILITARY: alloc.MILITARY ?? 14.28, THIEVERY: alloc.THIEVERY ?? 14.29,
        CHANNELING: alloc.CHANNELING ?? 14.29,
      });
    }
  }, [province]);

  const researchRateOptions = useMemo(
    () => province?.sciences?.researchRateOptions ?? [],
    [province?.sciences?.researchRateOptions]
  );

  const scienceTypes = useMemo(
    () => province?.sciences?.scienceTypes ?? [],
    [province?.sciences?.scienceTypes]
  );

  const unallocatedBooks = useMemo(
    () => Math.floor(province?.sciences?.unallocatedBooks ?? 0),
    [province?.sciences?.unallocatedBooks]
  );

  const investmentProgress = useMemo(
    () => province?.sciences?.investmentProgress ?? {},
    [province?.sciences?.investmentProgress]
  );

  const investments = useMemo(
    () => province?.sciences?.investments ?? [],
    [province?.sciences?.investments]
  );

  const calculatedValues = useMemo(() => {
    if (!province) return { booksPerTick: 0, goldCost: 0, costPercentage: 0 };

    if (researchRate === province.sciences.researchRate && province.sciences.costPreview) {
      const preview = province.sciences.costPreview;
      return { booksPerTick: preview.booksPerTick, goldCost: preview.actualGoldCost, costPercentage: preview.costAsPercentOfIncome };
    }

    const totalAcres = province.territory.totals.acres;
    const rateData = researchRateOptions.find((r) => r.value === researchRate);
    if (!rateData) return { booksPerTick: 0, goldCost: 0, costPercentage: 0 };

    const booksPerTick = Math.floor(totalAcres * rateData.bpa);
    const rawGoldCost = booksPerTick * rateData.gcPerBook;
    const schoolsCostModifier = province.sciences.costPreview?.schoolsCostModifier ?? 1.0;
    const goldCost = Math.floor(rawGoldCost * schoolsCostModifier);
    const dailyIncome = province.economy.lastTickChanges.goldIncome;
    const costPercentage = dailyIncome > 0 ? (goldCost / dailyIncome) * 100 : 0;

    return { booksPerTick, goldCost, costPercentage };
  }, [province, researchRate, researchRateOptions]);

  const totalBooksToInvest = useMemo(
    () => Object.values(booksToInvest).reduce((s, v) => s + v, 0),
    [booksToInvest]
  );
  const investmentValid = useMemo(
    () => totalBooksToInvest > 0 && totalBooksToInvest <= unallocatedBooks,
    [totalBooksToInvest, unallocatedBooks]
  );

  // SF-140 (mirrors SF-132's Buildings.tsx / SF-139's Explore.tsx reportError/reportSuccess
  // split): mobile routes feedback through the app-wide toast system; desktop keeps its existing
  // inline messages.
  const reportError = useCallback(
    (message: string) => {
      if (platform === 'mobile') {
        addToast(message, 'failure');
      } else {
        setSubmitError(message);
      }
    },
    [platform, addToast]
  );

  const reportSuccess = useCallback(
    (message: string) => {
      if (platform === 'mobile') {
        addToast(message, 'success');
      } else {
        setSuccessMessage(message);
      }
    },
    [platform, addToast]
  );

  const handleResearchRateChange = (value: string) => {
    setResearchRate(value);
    setSubmitError(null);
    setSuccessMessage(null);
  };

  const handleSubmitSettings = async () => {
    setSubmitError(null);
    setSuccessMessage(null);

    setIsSubmittingSettings(true);
    try {
      const request: ScienceSettingsRequest = { researchRate, allocation: { ...allocations } };
      const response = await api.post<ScienceSettingsResult>(
        `/api/worlds/${worldId}/provinces/${provinceId}/science`, request
      );

      if (response.error) {
        reportError(response.error);
      } else if (response.data) {
        reportSuccess(
          `Research settings updated. Producing ${formatNumber(calculatedValues.booksPerTick)} books/day for ${formatNumber(calculatedValues.goldCost)}gc.`
        );
        refresh();
      }
    } catch {
      reportError('Failed to update science settings. Please try again.');
    } finally {
      setIsSubmittingSettings(false);
    }
  };

  const handleBooksToInvestChange = useCallback((scienceType: string, value: number) => {
    setBooksToInvest((prev) => ({ ...prev, [scienceType]: value }));
  }, []);

  const handleSubmitInvestment = async () => {
    setSubmitError(null);
    setSuccessMessage(null);

    if (!investmentValid) {
      reportError(
        totalBooksToInvest === 0
          ? 'Enter at least 1 book to invest'
          : `Cannot invest ${totalBooksToInvest} books (only ${unallocatedBooks} available)`
      );
      return;
    }

    setIsSubmittingInvestment(true);
    try {
      const filteredInvestments: Record<string, number> = {};
      Object.entries(booksToInvest).forEach(([type, count]) => {
        if (count > 0) filteredInvestments[type] = count;
      });

      const request: InvestInScienceRequest = { booksToInvest: filteredInvestments };
      const response = await api.post<InvestScienceResponse>(
        `/api/worlds/${worldId}/provinces/${provinceId}/science/invest`, request
      );

      if (response.error) {
        reportError(response.error);
      } else if (response.data) {
        const result = response.data.result;
        if (result.success) {
          reportSuccess(
            `Invested ${formatNumber(result.totalBooksInvested)} books! ${formatNumber(result.remainingUnallocatedBooks)} books remaining.`
          );
          setBooksToInvest(EMPTY_BOOKS);
          refresh();
        } else {
          reportError(result.failureReason || 'Investment failed');
        }
      }
    } catch {
      reportError('Failed to invest books. Please try again.');
    } finally {
      setIsSubmittingInvestment(false);
    }
  };

  const handleFillStrategy = () => {
    if (!activeStrategy?.settings?.scienceAlloc || unallocatedBooks <= 0) return;
    const filled = computeFillStrategyBooks(activeStrategy.settings.scienceAlloc, unallocatedBooks);
    setBooksToInvest(filled);
  };

  const handleClearInvestment = () => {
    setBooksToInvest(EMPTY_BOOKS);
    setSubmitError(null);
    setSuccessMessage(null);
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-[50vh]">
        <Spinner size="lg" />
      </div>
    );
  }

  if (error || !province) {
    return (
      <div className="min-h-[50vh] flex items-center justify-center p-4">
        <div className="max-w-md w-full">
          <ErrorMessage message={error || 'Failed to load province'} />
        </div>
      </div>
    );
  }

  const shellProps: SciencesShellProps = {
    province,
    activeStrategy,
    strategyLoading,
    activeTab,
    setActiveTab,
    researchRate,
    booksToInvest,
    isSubmittingSettings,
    isSubmittingInvestment,
    submitError,
    successMessage,
    handleResearchRateChange,
    handleSubmitSettings,
    handleBooksToInvestChange,
    handleSubmitInvestment,
    handleFillStrategy,
    handleClearInvestment,
    researchRateOptions,
    scienceTypes,
    unallocatedBooks,
    investmentProgress,
    investments,
    calculatedValues,
    totalBooksToInvest,
    investmentValid,
  };

  return platform === 'mobile'
    ? <MobileSciencesShell {...shellProps} />
    : <DesktopSciencesShell {...shellProps} />;
}
